import { getTestAttributes } from '@chillibean/component-library';
import styled from 'styled-components';

import { dateFormat } from '../../../../__util__/dates';
import { isValidDate } from './dateHelpers';
import STATUS_MAP from './statusHelpers';
import { getProcedureUrl, isValidUrl } from './urlHelpers';

const renderFormattedAssets = (rawValue, assetEntry) => {
  if (rawValue.length === 0) return '-';
  const label = rawValue.join(',\n');

  return (
    <div label={label}>
      {rawValue.map((assetId, index) => {
        const assetUrl = `${getProcedureUrl(assetEntry)}?asset_id=${assetId}`;
        const isLastItem = index === rawValue.length - 1;

        return (
          <LinkWrap
            title={assetUrl}
            key={`${assetId}${index + 1}`}
            href={assetUrl}
            {...getTestAttributes(`asset-${assetId}-title`)}
            target="_blank"
            rel="noreferrer"
          >
            {assetId}
            {!isLastItem && ', '}
          </LinkWrap>
        );
      })}
    </div>
  );
};

const renderCompletedForms = (rawValue, assetEntry) => {
  if (rawValue.length === 0) return '-';
  const label = rawValue.join(',\n');

  return (
    <div label={label}>
      {rawValue.map((formId, index) => {
        const formUrl = `${getProcedureUrl(assetEntry)}/forms/${formId}`;
        const isLastItem = index === rawValue.length - 1;

        return (
          <LinkWrap
            title={formUrl}
            key={`${formId}${index + 1}`}
            href={formUrl.toString()}
            {...getTestAttributes(`form-${formId}-title`)}
            target="_blank"
            rel="noreferrer"
          >
            {formId}
            {!isLastItem && ', '}
          </LinkWrap>
        );
      })}
    </div>
  );
};

const renderDateValue = (rawValue, accessor = null) => {
  if (accessor === 'studyEvent|date' || accessor === 'date') {
    return dateFormat(rawValue, { withoutTime: true });
  }

  return dateFormat(rawValue, { withoutTime: false });
};

const renderGeneralDates = (rawValue) => {
  if (rawValue.length === 0) return '-';
  const dateValues = rawValue.map((date) => (!date || date === '' ? '-' : renderDateValue(date)));
  const label = dateValues.join('\n');

  return (
    <div label={label}>
      {dateValues.map((date, idx) => {
        return (
          <ArrayWrap key={`${date}${idx + 1}`} title={date}>
            {date}
          </ArrayWrap>
        );
      })}
    </div>
  );
};

const renderArrayValues = (rawValue) => {
  const label = rawValue.map((v) => v ?? '-').join('\n');

  return (
    <div label={label}>
      {rawValue.map((el, idx) => {
        let val = el;
        if (!el || el === '' || el === 'Not set') val = '-';

        return (
          <ArrayWrap key={`${el}${idx + 1}`} title={val}>
            {val}
          </ArrayWrap>
        );
      })}
    </div>
  );
};

const renderValidUrl = (rawValue, assetEntry) => (
  <a href={rawValue} {...getTestAttributes(`asset-${assetEntry.asset_id}-link`)} target="_blank" rel="noreferrer">
    {rawValue}
  </a>
);

export const formatValue = ({ rawValue, assetEntry, formattingEnabled, accessor }) => {
  if (accessor === 'assets') return renderFormattedAssets(rawValue, assetEntry);
  if (accessor === 'completedForms') return renderCompletedForms(rawValue, assetEntry);
  if (accessor === 'flaggedTasks|createdAt' || accessor === 'flaggedTasks|resolvedAt')
    return renderGeneralDates(rawValue);
  if (rawValue in STATUS_MAP) return STATUS_MAP[rawValue];
  if (isValidDate(rawValue)) return renderDateValue(rawValue, accessor);
  if (Array.isArray(rawValue)) return renderArrayValues(rawValue);
  if (!rawValue || rawValue === '' || rawValue.length === 0) return '-';
  if (!formattingEnabled || !Number.isNaN(Number(rawValue))) return rawValue;
  if (isValidUrl(rawValue)) return renderValidUrl(rawValue, assetEntry);

  return rawValue;
};

const LinkWrap = styled.a`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const ArrayWrap = styled.div`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

export default formatValue;
